/**
 * A function to create a log object for the 'homeNotificationCreatedNew' event (evid: 1125)
 *
 * Event description: a notification is created. Does not include all the target audience- since the
 *
 * @param {homeNotificationCreatedNewParams} params - The parameters for this event
 * @property {string} channel - Web / browser / mobile / SMS / email
 * @property {boolean} is_incognito - True when the notification will be sent via incognito
 * @property {boolean} is_muted - true if user muted notifications from the app (not from the os)
 * @property {string} correlation_id - request id
 * @property {string} initiator_type - type of the initiator (app,uuid,contact)
 * @property {string} notification_id - id for tracking notification
 * @property {string} initiator_id - id of the initiator (app,uuid,contact)
 * @property {string} notification_urgency - deprecated
 * @property {string} app_id - app that sent the notification
 * @property {string} topic - Hierarchical identifier for a stream of notifications.Used to define feed, aggregate notifications, settings are based on the topic, etc.
 * @property {string} contact_id - contact_id
 * @property {GUID} msid - msid of the notification
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @return {LogObject<homeNotificationCreatedNewParams>} LogObject<homeNotificationCreatedNewParams>
 */
export function homeNotificationCreatedNew(params) {
    return { evid: 1125, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'notificationsResolveAlertNotification' event (evid: 1128)
 *
 * Event description: Resolve notification or alert
 *
 * @param {notificationsResolveAlertNotificationParams} params - The parameters for this event
 * @property {string} resolve_type - resolve/ revoke
 * @property {string} notification_id - id for tracking notification
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @return {LogObject<notificationsResolveAlertNotificationParams>} LogObject<notificationsResolveAlertNotificationParams>
 */
export function notificationsResolveAlertNotification(params) {
    return { evid: 1128, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationCreationRequest' event (evid: 1129)
 *
 * Event description: a request for notification creation.
 * The target audience
 *
 * @param {pingNotificationCreationRequestParams} params - The parameters for this event
 * @property {string} template_component_id - the component id of the template (guid, component id as it appears in DevCenter)
 * @property {string} channel - Web / browser / mobile / SMS / email
 * @property {string} template_name - the name of the template as it appears in DevCenter
 * @property {boolean} is_incognito - True when the notification will be sent via incognito
 * @property {string} correlation_id - request id
 * @property {string} app_version - app version as it appears in DevCenter, the app version of the app which created the template
 * @property {string} initiator_type - type of the initiator (app,uuid,contact)
 * @property {string} notification_id - id for tracking notification
 * @property {string} initiator_id - id of the initiator (app,uuid,contact)
 * @property {string} setting_group - the setting group of the notification
 * @property {string} app_id - app that sent the notification
 * @property {string} topic - Hierarchical identifier for a stream of notifications.Used to define feed, aggregate notifications, settings are based on the topic, etc.
 * @property {string} contact_id - contact_id
 * @property {string} ping_template_id - a guid that will be updated in each app_version (could be changes in the component)
 * @property {string} template_description - the template description as it appears in DevCenter
 * @property {GUID} msid - site guid
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @return {LogObject<pingNotificationCreationRequestParams>} LogObject<pingNotificationCreationRequestParams>
 */
export function pingNotificationCreationRequest(params) {
    return { evid: 1129, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationSend' event (evid: 1130)
 *
 * Event description: Notification was sent to the user
 *
 * @param {pingNotificationSendParams} params - The parameters for this event
 * @property {string} channel - web/mobile/browser/sms/email
 * @property {string} provider - in case the channel is email: email provider [responsys/contacts_mailer/shoutout], in case channel is sms: channels when the notification sent to channels/sms gateway when the notifications sent to Twillio
 * @property {string} subaccount_id - subaccount_id
 * @property {string} correlation_id - correlation_id
 * @property {string} app_version - app_version
 * @property {string} notification_id - id for tracking notification
 * @property {string} token - token
 * @property {string} app_id - app_id of the vertical requested the notification.
 * @property {string} target - target
 * @property {string} contact_id - contact_id
 * @property {string} os_category - os_category
 * @property {string} groupNotificationId - An ID for a group of similar notifications clustered in feed
 * @return {LogObject<pingNotificationSendParams>} LogObject<pingNotificationSendParams>
 */
export function pingNotificationSend(params) {
    return { evid: 1130, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationFiltered' event (evid: 1133)
 *
 * Event description: After notification has created we check if we need to send it.
 * For each check this event is being sent (not only for filtered notification_ids).
 * We won't send the notification if the user doesn't have members area/oneapp etc.
 *
 * @param {pingNotificationFilteredParams} params - The parameters for this event
 * @property {string} channel - Web / browser / mobile / SMS / email
 * @property {string} correlation_id - request id
 * @property {string} initiator_type - type of the initiator (app,uuid,contact)
 * @property {string} reason - If it's filtered then this is the reason. f.e. - "no members area"
 * @property {string} notification_id - id for tracking notification
 * @property {boolean} isFilter - true if it's filtered and won't be sent. - relevant for web only and until 9.11
 * @property {string} initiator_id - id of the initiator (app_id,uuid,contact_id)
 * @property {string} token - the token of the mobile device
 * @property {string} app_id - app that sent the notification
 * @property {string} topic - Hierarchical identifier for a stream of notifications.Used to define feed, aggregate notifications, settings are based on the topic, etc.
 * @property {string} contact_id - contact_id
 * @property {GUID} msid - msid of the notification
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @return {LogObject<pingNotificationFilteredParams>} LogObject<pingNotificationFilteredParams>
 */
export function pingNotificationFiltered(params) {
    return { evid: 1133, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'androidNotificationReceived' event (evid: 1134)
 *
 * Event description: Technical BI which sent when ping api was called
 *
 * @param {androidNotificationReceivedParams} params - The parameters for this event
 * @property {string} correlation_id - request id
 * @property {string} audience_id - followers, site subscribers / site members
 * @property {string} initiator_type - initiator_type
 * @property {string} initiator_id - initiator_id
 * @property {string} topic - Hierarchical identifier for a stream of notifications.Used to define feed, aggregate notifications, settings are based on the topic, etc.
 * @property {GUID} msid - the msid triggered the call
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @return {LogObject<androidNotificationReceivedParams>} LogObject<androidNotificationReceivedParams>
 */
export function androidNotificationReceived(params) {
    return { evid: 1134, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'androidNotificationReceivedSrc19Evid1135' event (evid: 1135)
 *
 * Event description: Notification successfully arrived at the android device (but hasn't necessarily displayed in the OS notification center).  Useful for debugging when user complaints that notifications are not delivered.
 * (Had this event in 67:1031 - move it to Ping service, and add the notification_id).
 *
 * @param {androidNotificationReceivedSrc19Evid1135Params} params - The parameters for this event
 * @property {boolean} pushApproved - if all notifications are muted from the os. True if there is permission, False if there isn't permission.
 * @property {boolean} is_muted - if the specific channel of the notification is muted. True if muted, False if unmuted.
 * @property {boolean} is_displayed - True if displayed successfully, False if wasn't displayed (please ignore this field values until further update, it's WIP currently)
 * @property {string} payload - payload
 * @property {string} correlation_id - correlation_id
 * @property {string} reason - the error which caused the notification to not display
 * @property {string} notification_id - id for tracking notification
 * @return {LogObject<androidNotificationReceivedSrc19Evid1135Params>} LogObject<androidNotificationReceivedSrc19Evid1135Params>
 */
export function androidNotificationReceivedSrc19Evid1135(params) {
    return { evid: 1135, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingReviewSendingList' event (evid: 1136)
 *
 * Event description: Technical BI which sent when ping review the sending list of the recipients
 *
 * @param {pingReviewSendingListParams} params - The parameters for this event
 * @property {boolean} is_multilingual - is_multilingual
 * @property {string} correlation_id - request id
 * @property {string} audience_id - followers, site subscribers / site members
 * @property {string} result - sending status : filtered/success
 * @property {string} initiator_type - initiator_type
 * @property {string} reason - filtered reason: permissions/language
 * @property {string} initiator_id - initiator_id
 * @property {string} language - contact's language
 * @property {string} topic - Hierarchical identifier for a stream of notifications.Used to define feed, aggregate notifications, settings are based on the topic, etc.
 * @property {string} contact_id - contact_id
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @return {LogObject<pingReviewSendingListParams>} LogObject<pingReviewSendingListParams>
 */
export function pingReviewSendingList(params) {
    return { evid: 1136, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotifyCallSrc19Evid1160' event (evid: 1160)
 *
 * Event description: Technical BI which sent when ping api was called
 *
 * @param {pingNotifyCallSrc19Evid1160Params} params - The parameters for this event
 * @property {string} flow_type - The flow the notification came from to ping (API/automation)
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {string} external_id - an external id an app sets to join multiple API calls to one
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @property {string} app_id - the app related to the notification. through automations: the app related to the automation
 * through API: the app that made the request
 *
 * @property {string} topic_id - The topic guid as displayed in notification page preference (topic = toggle name ) . Hierarchical identifier for a stream of notifications.
 * @property {string} initiator_id - the ID of who initiated the notification
 * @property {string} initiator_type - the type of who initiated the notification (user/uou-contact/app)
 * @property {string} meta_template_id - the id of the approved template in meta. only when channel=WhatsApp
 * @property {string} caller_hash_number - The hash of a phone number displayed as the sender on WhatsApp
 * @property {GUID} msid - msid
 * @return {LogObject<pingNotifyCallSrc19Evid1160Params>} LogObject<pingNotifyCallSrc19Evid1160Params>
 */
export function pingNotifyCallSrc19Evid1160(params) {
    return { evid: 1160, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotifyCallAbortedSrc19Evid1161' event (evid: 1161)
 *
 * Event description: After Notify call, If the notification was filtered or aborted creates for each notification X site that is affected.
 *
 * @param {pingNotifyCallAbortedSrc19Evid1161Params} params - The parameters for this event
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {string} external_id - an external id an app sets to join multiple API calls to one
 * @property {string} app_id - the app related to the notification. through automations: the app related to the automation
 * through API: the app that made the request
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @property {string} initiator_id - the ID of who initiated the notification
 * @property {string} initiator_type - the type of who initiated the notification (user/uou-contact/app)
 * @property {string} meta_template_id - the id of the approved template in meta. only when channel=WhatsApp
 * @property {string} reason - The reason the notification was aborted
 * @property {GUID} msid - msid
 * @return {LogObject<pingNotifyCallAbortedSrc19Evid1161Params>} LogObject<pingNotifyCallAbortedSrc19Evid1161Params>
 */
export function pingNotifyCallAbortedSrc19Evid1161(params) {
    return { evid: 1161, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationCreationRequestSrc19Evid1162' event (evid: 1162)
 *
 * Event description: a request for notification creation. create for each notification_id and channel.
 *
 * @param {pingNotificationCreationRequestSrc19Evid1162Params} params - The parameters for this event
 * @property {string} notification_id - The unique identifier for a notification per site per recipient
 *
 * @property {string} channel - The channel the notification was sent through
 *
 * @property {string} flow_type - The flow the notification came from to ping (API/automation)
 *
 * @property {string} correlation_id - The unique identifier for a notification X site
 *
 * @property {string} external_id - an external id an app sets to join multiple API calls to one
 *
 * @property {string} topic_id - The topic guid as displayed in notification page preference (topic = toggle name ) . Hierarchical identifier for a stream of notifications.
 *
 * @property {string} template_id - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @property {string} template_name - A template of a notification (notifications are created based on a template, which defines the content, channels, behavior, etc.)
 * @property {string} app_id - the app related to the notification. through automations: the app related to the automation
 * through API: the app that made the request
 * @property {string} initiator_id - the ID of who initiated the notification
 * @property {string} initiator_type - the type of who initiated the notification (user/uou-contact/app)
 * @property {string} recipient_id - the ID of who the notification directed to
 * @property {string} recipient_type - the type of who the notification directed to (user/uou-contact/incognito)
 * @property {string} requested_locale - the language and dialect in which the notification was requested to be sent
 * @property {string} available_locale - the language and dialect in which the notification was available to be sent
 * @property {string} failure_reason - the reason the step failed
 * @property {GUID} msid - msid
 * @property {boolean} is_failed - true if the message failed to sent
 * @return {LogObject<pingNotificationCreationRequestSrc19Evid1162Params>} LogObject<pingNotificationCreationRequestSrc19Evid1162Params>
 */
export function pingNotificationCreationRequestSrc19Evid1162(params) {
    return { evid: 1162, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingFilteredBeforeSentSrc19Evid1163' event (evid: 1163)
 *
 * Event description: If the notification was filtered before sent and after request. create for each notification X receiver X channel that is filtered
 *
 * @param {pingFilteredBeforeSentSrc19Evid1163Params} params - The parameters for this event
 * @property {string} channel - The channel the notification was sent through
 * @property {string} correlation_id - The unique identifier for a notification X site
 *
 * @property {string} recipient_id - the ID of who the notification directed to
 *
 * @property {string} recipient_type - the type of who the notification directed to (user/uou-contact/incognito)
 * @property {string} device_id - The unique identifier for phone (is 2 phones has same uuid)
 * @property {string} reason - The reason the notification was filtered
 * @property {string} notification_id - The unique identifier for a notification per site per recipient
 * @property {GUID} msid - msid
 * @return {LogObject<pingFilteredBeforeSentSrc19Evid1163Params>} LogObject<pingFilteredBeforeSentSrc19Evid1163Params>
 */
export function pingFilteredBeforeSentSrc19Evid1163(params) {
    return { evid: 1163, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationSentSrc19Evid1164' event (evid: 1164)
 *
 * Event description: Notification was sent to the user (for success sent filter is_failed=False)
 *
 * @param {pingNotificationSentSrc19Evid1164Params} params - The parameters for this event
 * @property {string} notification_id - The unique identifier for a notification per site per recipient
 * @property {string} channel - The channel the notification was sent through
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {string} recipient_id - the ID of who the notification directed to
 * @property {string} recipient_type - the type of who the notification directed to (user/uou-contact/incognito)
 * @property {string} device_id - The unique identifier for phone (is 2 phones has same uuid)
 * @property {string} group_id - The group the notificication will be part of
 * @property {string} provider - the provider the notification was sent through
 * @property {string} geo - geo
 * @property {string} email_id - email_id sent from responsys
 * @property {boolean} is_failed - did the step fail
 * @property {string} failure_reason - the reason the step failed
 * @property {GUID} msid - msid
 * @property {boolean} is_quota_enforced - for WA messsages, True if quota enforced
 * @property {boolean} is_test_mode - true in cases of testing new capabilities(e.g. WA messages)
 * @return {LogObject<pingNotificationSentSrc19Evid1164Params>} LogObject<pingNotificationSentSrc19Evid1164Params>
 */
export function pingNotificationSentSrc19Evid1164(params) {
    return { evid: 1164, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingWhatsAppSentSrc19Evid1165' event (evid: 1165)
 *
 * Event description: an indicator sent from meta the message was sent(or failed to sent)
 *
 * @param {pingWhatsAppSentSrc19Evid1165Params} params - The parameters for this event
 * @property {string} notification_id - The unique identifier for a notification per site per recipient
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {string} meta_message_id - The ID as saved in meta
 * @property {DateTime} meta_date_created - the date the action actually occurred - the events are in delay
 * @property {GUID} msid - msid
 * @property {boolean} is_failed - true if message failed to sent
 * @property {string} failure_reason - reason of failure
 * @property {boolean} is_quota_enforced - for WA messsages, True if quota enforced
 * @property {boolean} is_test_mode - true in cases of testing new capabilities(e.g. WA messages)
 * @return {LogObject<pingWhatsAppSentSrc19Evid1165Params>} LogObject<pingWhatsAppSentSrc19Evid1165Params>
 */
export function pingWhatsAppSentSrc19Evid1165(params) {
    return { evid: 1165, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationReceivedSrc19Evid1166' event (evid: 1166)
 *
 * Event description: Notification successfully arrived at the device when channel= 'mobile-push' and provider='fcm' or channel= 'whatsapp'
 *
 * @param {pingNotificationReceivedSrc19Evid1166Params} params - The parameters for this event
 * @property {string} notification_id - The unique identifier for a notification per site per recipient
 * @property {string} channel - The channel the notification was sent through
 * @property {string} flow_type - The flow the notification came from to ping (API/automation)
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {string} device_id - The unique identifier for phone (is 2 phones has same uuid)
 * @property {DateTime} meta_date_created - the date the action actually occurred - the events are in delay
 * @property {GUID} msid - msid
 * @return {LogObject<pingNotificationReceivedSrc19Evid1166Params>} LogObject<pingNotificationReceivedSrc19Evid1166Params>
 */
export function pingNotificationReceivedSrc19Evid1166(params) {
    return { evid: 1166, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1167' event (evid: 1167)
 *
 * Event description: Includes information about the delivery status of sent WA message, main metrics- Cost.
 *
 *
 * @param {pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1167Params} params - The parameters for this event
 * @property {string} externalId - equivalent to correlation_id
 * @property {number} quotaDeducted - num deducted from quota
 * @property {string} metaMessageId - message_id provided by Meta
 * @property {string} messageCost - message cost(usd)
 * @property {string} senderGeo - sender geo
 * @property {number} quotaRemaining - # of messages remained in quota
 * @property {number} quotaLimit - Sites' package size
 * @property {string} recipientGeo - recipient geo
 * @property {number} quotaUse - cumulative quota usage
 * @property {string} sessionId - equivalent to notification_id
 * @property {DateTime} message_send_date - actual send date
 * @property {DateTime} actual_sent_date - actual send date
 * @return {LogObject<pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1167Params>} LogObject<pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1167Params>
 */
export function pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1167(params) {
    return { evid: 1167, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1168' event (evid: 1168)
 *
 * Event description: Includes information about the delivery status of sent WA message, main metrics- Cost.
 *
 *
 * @param {pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1168Params} params - The parameters for this event
 * @property {string} external_id - equivalent to correlation_id
 * @property {number} quota_deducted - num deducted from quota
 * @property {string} meta_message_id - message_id provided by Meta
 * @property {string} message_cost - message cost(usd)
 * @property {string} sender_geo - sender geo
 * @property {number} quota_remaining - # of messages remained in quota
 * @property {number} quota_limit - Sites' package size
 * @property {string} recipient_geo - recipient geo
 * @property {number} quota_use - cumulative quota usage
 * @property {string} session_id - equivalent to notification_id
 * @property {DateTime} actual_sent_date - actual send date
 * @property {string} msid - msid
 * @return {LogObject<pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1168Params>} LogObject<pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1168Params>
 */
export function pingWhatsAppDeliveryStatusFullDetailsSrc19Evid1168(params) {
    return { evid: 1168, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationsDisplayedSrc5Evid1930' event (evid: 1930)
 *
 * Event description: user opens the dialog and X notifications are being displayed , after scrolling another event will be sent with a new page number.
 * Exist when channal='mobile-feed'/ 'web-feed' / 'email' and provider = 'wix-emails' / 'whatsapp' and status from meta= read
 *
 * @param {pingNotificationsDisplayedSrc5Evid1930Params} params - The parameters for this event
 * @property {string} notification_id - The unique identifier for a notification per site per recipient if channel=feed then the id's are concat
 * @property {string} channel - The channel the notification was sent through
 * @property {string} flow_type - The flow the notification came from to ping (API/automation)
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {string} group_id - The group the notificication will be part of only exists when channel='mobile-feed'/'web-feed'
 * @property {DateTime} meta_date_created - the date the action actually occurred - the events are in delay
 * @property {GUID} msid - msid
 * @property {GUID} uuid - uuid
 * @return {LogObject<pingNotificationsDisplayedSrc5Evid1930Params>} LogObject<pingNotificationsDisplayedSrc5Evid1930Params>
 */
export function pingNotificationsDisplayedSrc5Evid1930(params) {
    return { evid: 1930, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingNotificationClickedSrc5Evid1931' event (evid: 1931)
 *
 * Event description: user clicks on a notification. exists when channel= 'mobile-feed'/ 'web-feed'/ 'mobile-push'/ 'web-push' / 'whatsapp'
 *
 * @param {pingNotificationClickedSrc5Evid1931Params} params - The parameters for this event
 * @property {string} notification_id - The unique identifier for a notification per site per recipient
 * if channel=feed then the id's are concat
 * @property {string} channel - The channel the notification was sent through
 * @property {string} flow_type - The flow the notification came from to ping (API/automation)
 * @property {string} correlation_id - The unique identifier for a notification X site
 * @property {GUID} msid - msid
 * @property {string} recipient_id - the ID of who the notification directed to
 * @property {string} device_id - The unique identifier for phone (is 2 phones has same uuid)
 * @property {string} group_id - The group the notificication will be part of only exists when channel='mobile-feed'/'web-feed'
 *
 * @property {string} clicktype - "click", "delete", "mark unread", "mark read", "dismiss", "undo delete"
 * @property {string} recipient_type - the type of who the notification directed to (user/uou-contact/incognito)
 * @return {LogObject<pingNotificationClickedSrc5Evid1931Params>} LogObject<pingNotificationClickedSrc5Evid1931Params>
 */
export function pingNotificationClickedSrc5Evid1931(params) {
    return { evid: 1931, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingAudiencePickerMigrationMonitorSrc5Evid1953' event (evid: 1953)
 *
 * Event description: An indicator of a lazy migration event. unique per msid, action_id, automation_id.
 *
 *
 * @param {pingAudiencePickerMigrationMonitorSrc5Evid1953Params} params - The parameters for this event
 * @property {string} actionId - action_id
 * @property {string} automationId - automation_id
 * @return {LogObject<pingAudiencePickerMigrationMonitorSrc5Evid1953Params>} LogObject<pingAudiencePickerMigrationMonitorSrc5Evid1953Params>
 */
export function pingAudiencePickerMigrationMonitorSrc5Evid1953(params) {
    return { evid: 1953, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingAudiencePickerModalOpenedSrc5Evid1954' event (evid: 1954)
 *
 * Event description: triggered when the audience picker modal opens.
 *
 *
 * @param {pingAudiencePickerModalOpenedSrc5Evid1954Params} params - The parameters for this event
 * @property {string} triggerId - what is the connected trigger for the action
 * @property {string} automationId - automation_id
 * @property {boolean} isAdvancedMode - True if the user is in advanced mode.
 * @return {LogObject<pingAudiencePickerModalOpenedSrc5Evid1954Params>} LogObject<pingAudiencePickerModalOpenedSrc5Evid1954Params>
 */
export function pingAudiencePickerModalOpenedSrc5Evid1954(params) {
    return { evid: 1954, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingAudiencePickerCheckboxClickedSrc5Evid1955' event (evid: 1955)
 *
 * Event description: triggered when the user clicks on checkbox within the picker modal
 *
 * @param {pingAudiencePickerCheckboxClickedSrc5Evid1955Params} params - The parameters for this event
 * @property {string} triggerId - what is the connected trigger for the action
 * @property {string} automationId - automation_id
 * @property {string} clickType - 'generic' for known types(Contacts, segemnts etc...).
 * 'custom' for additional providers
 * @property {string} clickName - of generic - click name, else provider name(if known)
 * @property {string} action - add/remove
 * @return {LogObject<pingAudiencePickerCheckboxClickedSrc5Evid1955Params>} LogObject<pingAudiencePickerCheckboxClickedSrc5Evid1955Params>
 */
export function pingAudiencePickerCheckboxClickedSrc5Evid1955(params) {
    return { evid: 1955, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingAudiencePickerModalClickedSrc5Evid1956' event (evid: 1956)
 *
 * Event description: triggered when the user clicks on the picker modal, excluding checkbox
 *
 * @param {pingAudiencePickerModalClickedSrc5Evid1956Params} params - The parameters for this event
 * @property {string} triggerId - what is the connected trigger for the action
 * @property {string} automationId - automation_id
 * @property {string} clickType - Modal (apply,X etc...)
 * list(for open recipients lists)
 * @property {string} clickName - if modal - click name.if list - provider name.
 * @property {string} providerId - if open list - provider id
 * @return {LogObject<pingAudiencePickerModalClickedSrc5Evid1956Params>} LogObject<pingAudiencePickerModalClickedSrc5Evid1956Params>
 */
export function pingAudiencePickerModalClickedSrc5Evid1956(params) {
    return { evid: 1956, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingAudiencePickerComponentLoadedSrc5Evid1960' event (evid: 1960)
 *
 * Event description: triggered when the audience picker component(panel) is rendered.
 *
 *
 * @param {pingAudiencePickerComponentLoadedSrc5Evid1960Params} params - The parameters for this event
 * @property {string} triggerId - what is the connected trigger for the action
 * @property {string} automationId - automation_id
 * @property {boolean} isAdvancedMode - True if the user is in advanced mode.
 * @return {LogObject<pingAudiencePickerComponentLoadedSrc5Evid1960Params>} LogObject<pingAudiencePickerComponentLoadedSrc5Evid1960Params>
 */
export function pingAudiencePickerComponentLoadedSrc5Evid1960(params) {
    return { evid: 1960, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'notificationShowNotification' event (evid: 271)
 *
 * Event description: user opens the dialog and X notifications are being displayed , after scrolling another event will be sent with a new page number
 *
 * @param {notificationShowNotificationParams} params - The parameters for this event
 * @property {boolean} is_high_priority - true / false
 * @property {string} feed_name - All sites/This site /float
 * @property {string} channel - Web / browser / mobile / SMS / email
 * @property {string} source - where the display was triggered from: site_level / account_level / home_site_level
 * @property {GUID} correlation_id - correlation_id
 * @property {string} view_type - view_type
 * @property {string} notification_ids - all notifications that were shown in the page ( separated with a comma)
 * @property {string} origin - MSID of the site the user is on, my-sites if on Site List. Later on, also account-settings, and other account-level pages.
 * @property {string} mode - online / cashed (in case of no internet)
 * @property {string} correlationId - correlation_id
 * @property {string} contact_id - contact_id guid
 * @property {string} platform - platform
 * @property {string} notificationid - deprecated
 * @property {GUID} msid - msid
 * @property {number} page_number - page number - each page is limited to 30 notifications
 * @return {LogObject<notificationShowNotificationParams>} LogObject<notificationShowNotificationParams>
 */
export function notificationShowNotification(params) {
    return { evid: 271, src: 5, endpoint: '', params };
}
/**
 * A function to create a log object for the 'notificationClickOnNotification' event (evid: 272)
 *
 * Event description: user clicks on a notification and opens app (test)
 *
 * @param {notificationClickOnNotificationParams} params - The parameters for this event
 * @property {boolean} is_high_priority - true / false
 * @property {string} feed_name - Sub category of the channel (All Sites/This Site /Float/Oneapp notification center/ mobile os)
 * @property {string} clickOrigin - deprecated
 * @property {string} channel - Web / browser / mobile / SMS / email
 * @property {GUID} correlation_id - correlation_id
 * @property {string} click_type - mark read/mark unread/click/delete
 * @property {string} view_type - view_type
 * @property {string} notification_id - deprecated
 * @property {string} origin - (Msid/my-site) MSID of the site the user is on, my-sites if on Site List. Later on, also account-settings, and other account-level pages.
 * @property {string} correlationId - correlation_id
 * @property {string} contact_id - contact_id
 * @property {string} platform - platform
 * @property {string} notificationid - notificationid
 * @property {GUID} msid - msid of the notification
 * @property {string} deep_link - deep_link
 * @property {string} grouped_notifications - list of notification ids contain in this notification. if the notification is not grouped this field will be null
 * @return {LogObject<notificationClickOnNotificationParams>} LogObject<notificationClickOnNotificationParams>
 */
export function notificationClickOnNotification(params) {
    return { evid: 272, src: 5, endpoint: '', params };
}
//# sourceMappingURL=index.js.map